import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { InstanceResponseDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { BaseInstanceResponseDto } from '@cloud-twin/master-operator/models/master-operator-instance';
import { MasterOperatorType } from '@cloud-twin/master-operator/models/master-operator-type';
import { MasterOperatorService } from '@cloud-twin/master-operator/services/master-operator.service';
import { GenericCardSettings } from '@common-modules/shared-component/generic-card/generic-card-settings';
import { DragListSettings } from '@common-modules/shared/core/drag-list-virtual/drag-list-settings';
import { SelectDragListSettings } from '@common-modules/shared/core/select-drag-list-virtual/select-drag-list-settings';
import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { DataBindingFilters } from '@common-modules/shared/filters/component-filters/data-binding-filters';
import { LocalizationHelperService } from '@common-modules/shared/localization/localization-helper.service';
import { SelectOption } from '@common-modules/shared/model/shared/select-option';
import { SpinnerService } from '@common-modules/wlm-spinner/spinner.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { finalize, of, switchMap } from 'rxjs';
import { ModelVersionSharedService } from '../services/model-version-shared.service';
import { ModelVersionService } from '../services/model-version.service';

const COMPONENT_SELECTOR = 'tc-models-selection';
@UntilDestroy()
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './models-selection.component.html',
  styleUrl: './models-selection.component.scss',
  standalone: false,
})
export class ModelsSelectionComponent implements OnInit {
  readonly T_SCOPE = `${TranslateScopes.ModelVersion}.${COMPONENT_SELECTOR}`;

  @Input() widgetInstanceKey: string;
  @Input() gridName: string;
  @Input() subscriptionTag = 'default';

  @Output() selectedElementChange = new EventEmitter<InstanceResponseDto>();

  private _instanceElementFieldId = 'instanceId';
  private _instanceElementFieldName = 'name';
  private _selectedFieldName = 'instanceId';
  private _pagesize = 50;

  instances: InstanceResponseDto[];
  private setLoading: (loading: boolean) => void;

  settings: DragListSettings;
  cardSettings: GenericCardSettings<any>;

  emptyFilters: DataBindingFilters;
  instanceOptions: SelectOption<string>[];
  selectedItem: InstanceResponseDto;

  constructor(
    private _masterOperatorService: MasterOperatorService,
    private _spinnerService: SpinnerService,
    private readonly _localization: LocalizationHelperService,
    private readonly _modelVersionService: ModelVersionService,
    private readonly _modelVersionSharedService: ModelVersionSharedService,
    private readonly _dialogService: DialogService,
    private readonly _translate: TranslateService
  ) {
    this.setLoading = this._spinnerService.buildSetLoadingFn();
  }

  ngOnInit(): void {
    this.prepareListsSettings();
  }

  onSelectItem(selectedItem: InstanceResponseDto) {
    this.selectedItem = selectedItem;
    this.selectedElementChange.emit(selectedItem);
  }

  removeInstance(instanceId: string): void {
    this.instances = this.instances.filter((instance) => instance.instanceId !== instanceId);
  }

  updateSnapshotsOfInstances(instanceId: string, snapshotIds: string[]) {
    this.instances = this.instances.map((instance) => {
      if (instance.instanceId !== instanceId) {
        return instance;
      }

      const clonedInstance = instance.clone();
      clonedInstance.snapshots = instance.snapshots?.filter(
        (snapshot) => !snapshotIds.includes(snapshot.snapshotId)
      );

      return clonedInstance;
    });
  }

  updateScenariosOfSnapshot(instanceId: string, snapshotId: string, scenarioIds: string[]) {
    this.instances = this.instances.map((instance) => {
      if (instance.instanceId !== instanceId) {
        return instance;
      }

      const clonedInstance = instance.clone();

      if (clonedInstance.snapshots) {
        clonedInstance.snapshots = clonedInstance.snapshots.map((snapshot) => {
          if (snapshot.snapshotId !== snapshotId) {
            return snapshot;
          }

          const clonedSnapshot = snapshot.clone();
          clonedSnapshot.scenarios = snapshot.scenarios?.filter(
            (scenario) => !scenarioIds.includes(scenario.scenarioId)
          );

          return clonedSnapshot;
        });
      }

      return clonedInstance;
    });
  }

  private prepareListsSettings() {
    this.setLoading(true);
    this._localization.get(`${this.T_SCOPE}.realtime-status`).subscribe((translations) => {
      this._masterOperatorService
        .getInstances({ type: MasterOperatorType.Master })
        .pipe(finalize(() => this.setLoading(false)))
        .subscribe((activeInstances) => {
          activeInstances.forEach((instance) => {
            instance.name = this._masterOperatorService.instanceReadableLabel(
              instance as BaseInstanceResponseDto
            );
          });
          let instances = activeInstances;
          this.instances = instances;
          this.settings = new SelectDragListSettings({
            isLocal: true,
            pageSize: this._pagesize,
            isReadOnly: false,
            scrollId: this.widgetInstanceKey,
            selectedFieldName: this._selectedFieldName,
            filtrableFields: [this._instanceElementFieldId, this._instanceElementFieldName],
          });
          this.cardSettings = new GenericCardSettings<InstanceResponseDto>({
            title1Fn: (model) => {
              return model.name;
            },
            subtitleFn: (model) => {
              return model.url ? model.url : '';
            },
            extraInfoFn: (model) => {
              return model.realTimeId ? translations.realtime : translations['not-realtime'];
            },
          });
        });
    });
  }

  deleteInstance(): void {
    const confirmMessageKey = this.getConfirmDeleteMessage('model.singular');
    this._dialogService
      .showTranslatedDialogMessage({
        translateKey: confirmMessageKey,
        icon: 'warning',
      })
      .pipe(
        switchMap((result) => {
          if (result.result) {
            this.setLoading(true);
            return this._modelVersionService.deleteInstance(this.selectedItem.instanceId);
          } else {
            return of(null);
          }
        }),
        finalize(() => {
          this.setLoading(false);
        })
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this._modelVersionSharedService.removeInstance(this.selectedItem.instanceId);
            this._modelVersionSharedService.removeScenarios(result);
            this._dialogService.showEntityActionSnackBar('delete', 'configuration');
          }
        },
        error: (error) => {
          this._dialogService.showErrorMessage(error);
        },
      });
  }

  getConfirmDeleteMessage(entityName: string): string {
    const entityNameTranslated = this._translate.instant(`common.entities.${entityName}`);
    return this._translate.instant(this.getConfirmDeleteMessageKey(), {
      entityName: entityNameTranslated,
    });
  }

  private getConfirmDeleteMessageKey(): string {
    return 'core.messages.delete-confirm';
  }
}
